import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";
import CustomSelect from "../../component/CustomSelect";
import DataTable from "../../component/DataTable";
import ApiRequest from "../../helper/ApiRequest";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import Icons from "../../myIcon/Icons";
import { StatusData, formatStatus } from "./RbacOptions";

const endpoints = ApiEndpoints.rbac;

const defaultForm = {
  name: "",
  slug: "",
  description: "",
  status: "1",
};

function PermissionModal({ show, onHide, permissionId, onSaved }) {
  const [form, setForm] = useState(defaultForm);
  const isEdit = Boolean(permissionId);

  useEffect(() => {
    if (!show) return;
    if (!permissionId) {
      setForm(defaultForm);
      return;
    }

    ApiRequest("GET", `${endpoints.permissionEdit}${permissionId}`)
      .then((response) => setForm({
        name: response.permission?.name || "",
        slug: response.permission?.slug || "",
        description: response.permission?.description || "",
        status: String(response.permission?.status ?? "1"),
      }))
      .catch(() => toast.error("Unable to fetch permission."));
  }, [show, permissionId]);

  const toSlug = (str) =>
    str
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/[\s_]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      // Auto-generate slug from name when creating (not editing)
      if (name === "name" && !isEdit) {
        next.slug = toSlug(value);
      }
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast.error("Permission name is required.");
      return;
    }

    try {
      const endpoint = isEdit ? `${endpoints.permissionUpdate}${permissionId}` : endpoints.permissionCreate;
      const response = await ApiRequest("POST", endpoint, form);
      if (response.error) {
        toast.error(response.message);
        return;
      }
      toast.success(response.message);
      onSaved();
      onHide();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to save permission.");
    }
  };

  return (
    <Modal className="add-modal-area" show={show} onHide={onHide} size="md" centered>
      <Modal.Body>
        <button className="modal-close-button" onClick={onHide}><Icons name="cross" /></button>
        <form onSubmit={handleSubmit}>
          <div className="add-page-sections">{isEdit ? "Edit Permission" : "Create Permission"}</div>
          <div className="add-page-inputs">
            <div className="label-wrap-add"><label>Name<small>*</small></label></div>
            <input name="name" value={form.name} className="form-control" placeholder="Enter permission name" onChange={handleChange} required />
          </div>
          <div className="add-page-inputs">
            <div className="label-wrap-add"><label>Slug</label></div>
            <input name="slug" value={form.slug} className="form-control" placeholder="auto-generated if blank" onChange={handleChange} />
          </div>
          <div className="add-page-inputs">
            <div className="label-wrap-add"><label>Description</label></div>
            <textarea name="description" value={form.description} className="form-control" placeholder="Enter description" onChange={handleChange} />
          </div>
          <div className="add-page-inputs">
            <div className="label-wrap-add"><label>Status<small>*</small></label></div>
            <CustomSelect name="status" options={StatusData} defaultValue={form.status} onChange={handleChange} />
          </div>
          <button type="submit" className="default-btn">save<span></span></button>
        </form>
      </Modal.Body>
    </Modal>
  );
}

export default function Permissions() {
  const [data, setData] = useState([]);
  const [permissionId, setPermissionId] = useState("");
  const [modalShow, setModalShow] = useState(false);

  const dataFetch = async () => {
    try {
      const response = await ApiRequest("GET", endpoints.permissionList);
      setData((response.permissions || []).map((item) => ({ ...item, statusText: formatStatus(item.status) })));
    } catch (error) {
      toast.error("Unable to fetch permissions.");
    }
  };

  useEffect(() => {
    dataFetch();
  }, []);

  const handleDelete = async (permission_id) => {
    if (!window.confirm("Delete this permission?")) return;
    const response = await ApiRequest("POST", endpoints.permissionDelete, { permission_id });
    toast.success(response.message);
    dataFetch();
  };

  const openCreate = () => {
    setPermissionId("");
    setModalShow(true);
  };

  return (
    <>
      <AdminHeader pagename={{ name: "User Management", user: "Admin", innerpage: "User Management / Permissions" }} />
      <div className="add-btn-section">
        <Link className="admin-add-btn" onClick={openCreate}> Add +</Link>
        <DataTable
          data={data}
          tableHeader={["Date", "Name", "Slug", "Status"]}
          tbody={["createdAt", "name", "slug", "statusText"]}
          setModalShow={setModalShow}
          setId={setPermissionId}
          onDelete={handleDelete}
        />
      </div>
      <PermissionModal show={modalShow} permissionId={permissionId} onHide={() => setModalShow(false)} onSaved={dataFetch} />
    </>
  );
}
